import type { RosterRow as Row } from '../model/roster.js';
import type { Selection } from '../model/selection.js';

/**
 * One roster row (design 252–288).
 *
 * ## Must-not #5, in the one place it is decided
 *
 * **There is one row component and it takes one shape.** A phantom and a real
 * contact are both a `RosterRow` built by `contactRow`, so they take the same
 * branch, produce the same elements and carry the same attributes — the DOM
 * differs in the alias and nowhere else. A second component "for phantoms"
 * cannot exist, because nothing on the wire says which is which; the point of
 * writing it this way is that nothing *later* can grow one either without
 * inventing the distinction first.
 *
 * Ordering is `hash(alias)`, applied in `model/roster.ts` before the rows reach
 * here. This component never sees an index and has no key derived from one.
 *
 * ## Must-not #2
 *
 * The id column renders `row.id`, which for a contact is the per-observer alias
 * — the only enemy identifier the client has ever been told. There is no path
 * from here to `friendly[]`.
 */

export interface RosterRowProps {
  readonly row: Row;
  readonly selected: boolean;
  readonly pinned: boolean;
  readonly batched: boolean;
  readonly onSelect: (selection: Selection) => void;
  readonly onToggleBatch: (id: string) => void;
  readonly onContextMenu: (row: Row, x: number, y: number) => void;
  /**
   * The design's two builder gestures: ⇧ fills the slot the row naturally
   * belongs in, ⌥ forces the target slot. Both are on the hotkey map, and both
   * were unbound until Phase 3 gave them a builder to fill.
   */
  readonly onFillNatural?: (id: string) => void;
  readonly onFillTarget?: (id: string) => void;
  /**
   * The cursor is on this row, or on its marker on the map.
   *
   * One state for both, so the rail and the canvas ring the same unit whichever
   * one the cursor is over — the design's own reciprocal highlight. It is set on
   * enter and leave only, never on move, so a hover across the roster costs two
   * renders and not sixty.
   */
  readonly hovered?: boolean;
  readonly onHover?: (id: string | null) => void;
}

export function RosterRowView(props: RosterRowProps): JSX.Element {
  const { row } = props;
  const selects = row.selects;
  // Side colours, and only side colours. A network is ours, so it is blue like
  // every other thing of ours; the `FRIENDLY NET` group heading is what tells it
  // apart from an asset. It used to be cyan, which made every network id read as
  // permanently selected now that cyan means exactly one thing.
  const idColour = row.subject === 'CONTACT' ? 'var(--red)' : 'var(--blue)';

  return (
    <div
      data-testid="roster-row"
      data-row-id={row.id}
      data-row-subject={row.subject}
      data-row-group={row.group}
      onPointerEnter={() => props.onHover?.(row.id)}
      onPointerLeave={() => props.onHover?.(null)}
      style={{
        borderBottom: '1px solid var(--rule-faint)',
        // Accent, not primary: this row *is* the selection, and the chip it produces in
        // the status row has always been cyan. Two colours for one act of picking was
        // the loudest of the amber jobs that were not acts.
        background: props.selected ? 'var(--accent-tint)' : 'transparent',
        borderLeft: '2px solid ' + (props.selected ? 'var(--accent)' : 'transparent'),
        boxShadow: props.hovered === true ? 'inset 0 0 0 1px var(--text-3)' : undefined,
        opacity: row.status === 'DESTROYED' ? 0.55 : 1,
      }}
    >
      <Hit
        selectable={selects !== null}
        selected={props.selected}
        draggable={selects !== null}
        onDragStart={(event) => event.dataTransfer.setData('text/plain', row.id)}
        onClick={(event) => {
          // Four gestures on one click, and the order is the design's own:
          // ⌘/Ctrl batches, ⌥ forces the target slot, ⇧ takes the natural slot,
          // and a bare click selects. The same click that selects must not also
          // batch — an operator adding a fifth unit to a batch has not asked to
          // look at it — and the same is true of filling a slot.
          if (event.metaKey || event.ctrlKey) props.onToggleBatch(row.id);
          else if (event.altKey) props.onFillTarget?.(row.id);
          else if (event.shiftKey) props.onFillNatural?.(row.id);
          else if (selects !== null) props.onSelect(selects);
        }}
        onContextMenu={(event) => {
          event.preventDefault();
          props.onContextMenu(row, event.clientX, event.clientY);
        }}
      >
        {(props.pinned || props.batched) && (
          <span
            data-testid="roster-mark"
            title={props.batched ? 'BATCH' : 'PINNED'}
            style={{
              ...MONO,
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 13,
              height: 13,
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1,
              border: '1px solid ' + (props.batched ? 'var(--accent)' : 'var(--primary)'),
              color: props.batched ? 'var(--accent)' : 'var(--primary)',
            }}
          >
            {props.batched ? 'B' : 'P'}
          </span>
        )}

        {/* The identifier is never truncated. It is the thing an operator says
            out loud across a room, and half of `BLUE-INTERCEPTOR-AURELIA` names
            nothing. */}
        <span
          data-testid="roster-id"
          style={{ ...MONO, flex: 'none', fontSize: 11, fontWeight: 600, color: idColour }}
        >
          {row.id}
        </span>

        {row.tasked && (
          <span
            data-testid="roster-tasked"
            style={{
              ...MONO,
              flex: 'none',
              padding: '1px 5px',
              fontWeight: 600,
              letterSpacing: '.08em',
              background: 'var(--primary-tint)',
              color: 'var(--primary)',
            }}
          >
            TSK
          </span>
        )}

        <span
          data-testid="roster-zone"
          style={{
            ...MONO,
            flex: 'none',
            width: 56,
            textAlign: 'right',
            // A zone longer than 56px was sliced mid-glyph by an ancestor's
            // `overflow: hidden` — 7px gone with nothing to say it had gone, and
            // 3px of it pushed the whole rail into a horizontal scroll. An
            // ellipsis is the same truncation admitting to itself.
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--text-3)',
          }}
        >
          {row.zone}
        </span>

        {/* Always its own line, indented under the id.
            At 300 px a 24-character id and a type name do not share a line, and
            the design's short mock ids (`CSG-5`, `H-114`) are what let its row
            be one. Making the wrap *conditional* on length was worse than either
            fixed shape: half the rows broke and half did not, and a list you
            scan is a list whose rows are the same shape. The design's own row is
            `min-height`, not `height`, which is the affordance for exactly this. */}
        <span
          style={{
            flex: '1 0 100%',
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            minWidth: 0,
            lineHeight: '15px',
          }}
        >
          {row.subject === 'ASSET' ? (
            <span
              data-testid="roster-status"
              style={{
                ...MONO,
                flex: 'none',
                width: STATE_COL,
                fontWeight: 600,
                color: statusColour(row.status),
              }}
            >
              {statusToken(row.status)}
            </span>
          ) : (
            <span
              data-testid="roster-rung"
              style={{
                ...MONO,
                flex: 'none',
                width: STATE_COL,
                fontWeight: 600,
                color: 'var(--text-3)',
              }}
            >
              {row.rung ?? ''}
            </span>
          )}
          <span
            data-testid="roster-name"
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: 'var(--font-prose)',
              fontSize: 12,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'var(--text-2)',
            }}
          >
            {row.name}
          </span>
        </span>
      </Hit>
    </div>
  );
}

/**
 * The row's hit area.
 *
 * A row that selects something is a `<button>`; a row that does not is a plain
 * element with no pointer affordance and no focus stop. That is the whole of
 * "self-evident from their affordance" — a caption saying which rows are
 * clickable would be the console explaining itself.
 */
function Hit(props: {
  readonly selectable: boolean;
  readonly selected: boolean;
  readonly draggable: boolean;
  readonly onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  readonly onClick: (event: React.MouseEvent<HTMLElement>) => void;
  readonly onContextMenu: (event: React.MouseEvent<HTMLElement>) => void;
  readonly children: React.ReactNode;
}): JSX.Element {
  const style = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0 8px',
    width: '100%',
    minHeight: 26,
    padding: '4px 12px 5px',
    background: 'transparent',
    border: 0,
    textAlign: 'left',
    cursor: props.selectable ? 'pointer' : 'default',
  } as const;

  if (!props.selectable) {
    return (
      <div onContextMenu={props.onContextMenu} style={style}>
        {props.children}
      </div>
    );
  }
  return (
    <button
      type="button"
      aria-current={props.selected ? 'true' : undefined}
      draggable={props.draggable}
      onDragStart={props.onDragStart}
      onClick={props.onClick}
      onContextMenu={props.onContextMenu}
      style={style}
    >
      {props.children}
    </button>
  );
}

/**
 * The word, not the first three letters of it.
 *
 * This used to be `status.slice(0, 3)`, which invented a vocabulary: the brief
 * teaches NOMINAL / DEGRADED / OFFLINE / DESTROYED and the roster showed NOM /
 * DEG / OFF / DES. The detection ladder was worse — HIDDEN / FOUND / FIXED /
 * TRACKED became HID / FOU / FIX / TRA, and `FOU` and `FIX` differ by one glyph
 * at 11px in a rail that had roughly 180px of unused width beside them.
 *
 * A player twenty minutes into their first exercise should not have to learn a
 * second name for something they were taught twenty minutes ago.
 */
/** Wide enough for DESTROYED, the longest word either column carries. */
const STATE_COL = 62;

function statusToken(status: string | null): string {
  return status ?? '';
}

function statusColour(status: string | null): string {
  switch (status) {
    case 'NOMINAL':
      return 'var(--ok)';
    case 'DEGRADED':
      return 'var(--warn)';
    case 'OFFLINE':
    case 'DESTROYED':
      return 'var(--red-text)';
    default:
      return 'var(--text-3)';
  }
}

const MONO = {
  fontFamily: 'var(--font-data)',
  fontSize: 11,
  letterSpacing: '.04em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;
